/* ==========================================================================
   woi-wall.js — behaviour for sections/woi-wall.liquid
   --------------------------------------------------------------------------
   The Words of Intention wall. Same behaviour as iq-gallery02.js, driven by
   the word_card metaobject instead of a collection.

   Five things worth knowing before editing:

   1. The wall is built here, not in Liquid. Liquid prints flat .wim__cell
      children; this makes the .wim__col flex columns and deals the cells in
      shortest-column-first, then closes each short column with a measured
      filler. A CSS grid cannot do it — auto-placement never backfills.
   2. Order is ALPHABETICAL BY TITLE. A new Word slots itself in; nothing has
      to be renumbered. data-sort is only the tie-break.
   3. The card's markup is NOT built here. It is real Liquid HTML in a hidden
      .wim__store div, copied in on open, so the rich text stays rich text
      instead of being escaped through a JSON blob.
   4. Card images are data-src with NO loading="lazy". Lazy on an
      already-deferred image never resolves — the heuristic runs while the
      element is still hidden and the image simply never loads. The card's
      Liquid prints ONE url at width=1400 and no srcset, so the sizing, the
      warming and the decode gate all happen here — see "images" below.
   5. The ink dots drive four things: the mark colour, the paper behind it,
      which wall image is shown, and where the two frame buttons point.
   ========================================================================== */

(function () {
  'use strict';

  var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 200); };

  function svgIcon(d) {
    return '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="' + d +
      '" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }
  var ICON_PREV = svgIcon('M9 2L4 7l5 5');
  var ICON_NEXT = svgIcon('M5 2l5 5-5 5');
  var ICON_CLOSE = svgIcon('M3 3l8 8M11 3l-8 8');

  /* -------------------------------------------------------------- images */

  /* Measured 2026-08-17 on the card: the wall view pulled width=1400 = 575 KB
     into a 530 px box, with no srcset to choose anything smaller, and the
     fetch did not START until the tab was clicked — 1004 ms to paint on a
     WARM cache. Three things fix that, all here rather than in the Liquid:
     size the url to the box, warm it before the click, and hold the outgoing
     view until the new one has decoded instead of showing an empty panel. */

  var WLADDER = [400, 600, 800, 1000, 1200, 1400];

  function stepFor(px) {
    for (var i = 0; i < WLADDER.length; i++) { if (WLADDER[i] >= px) return WLADDER[i]; }
    return WLADDER[WLADDER.length - 1];
  }

  /* Rewrite ?width= only. A url without that parameter is not a Shopify image
     render — the Word view is a CSS mask, not a photograph — so leave it. */
  function sizedSrc(url, px) {
    if (!url) return '';
    try {
      var u = new URL(url, location.href);
      if (!u.searchParams.get('width')) return url;
      u.searchParams.set('width', String(stepFor(px)));
      return u.href;
    } catch (e) { return url; }
  }

  /* Measure the ART COLUMN, never the image. A view that is not on has no
     box, and warming has to agree with showing — ask the image and you get
     0 while hidden, then a real number after, which is two downloads. */
  function artPx(el) {
    var w = 0, host;
    if (el && el.closest) {
      host = el.closest('.wim__art');
      if (host) w = host.getBoundingClientRect().width;
      if (!w) {
        host = el.closest('.wim-lb__panel');
        if (host) w = host.getBoundingClientRect().width * 0.5;
      }
    }
    if (!w) w = 560;
    return Math.round(w * Math.min(window.devicePixelRatio || 1, 2));
  }

  function woiWall(root, cfg) {
    var grid = root.querySelector('.wim__grid');
    if (!grid) return;

    var cells = Array.prototype.slice.call(grid.querySelectorAll('.wim__cell'));
    if (!cells.length) return;

    /* Alphabetical by the Word itself, so a Word added later lands in the
       right place with no renumbering anywhere. */
    cells.sort(function (a, b) {
      var wa = a.getAttribute('data-word') || '';
      var wb = b.getAttribute('data-word') || '';
      var c = wa.localeCompare(wb, undefined, { sensitivity: 'base' });
      if (c) return c;
      return (parseInt(a.getAttribute('data-sort'), 10) || 0) - (parseInt(b.getAttribute('data-sort'), 10) || 0);
    });

    var CARBON = cfg.carbon || '#2B2B2B';
    var LINEN = cfg.linen || '#EDE8DC';
    var PAPER_CARBON = cfg.paperCarbon || '#EDE8DC';
    var PAPER_LINEN = cfg.paperLinen || '#2B2B2B';
    var ACCENT = cfg.accent || '#00FFBF';
    var CYCLE = cfg.cycleMs || 0;
    var CAMK = cfg.camScale || 100;
    var FLAK = cfg.flashScale || 100;
    var L = cfg.labels || {};
    var DEFAULT_PAPER = cfg.defaultPaper === 'carbon' ? 'carbon' : 'linen';

    var fillerTpl = Array.prototype.slice.call(root.querySelectorAll('.wim__filler-src .wim__filler-one'));
    var LINES = fillerTpl.map(function (t) {
      var el = t.querySelector('.wim__filler-text');
      return el ? el.textContent : '';
    }).filter(Boolean);

    var columns = [];
    var busy = false;
    var cycleTimer = null;

    /* ---------------------------------------------------------------- wall */

    function colCount() {
      var v = parseInt(getComputedStyle(grid).getPropertyValue('--wim-cols'), 10);
      return (v && v > 0) ? v : 1;
    }

    /* A filler is a panel with the flash lamp above the line and the camera
       below it. Both are real elements, so the writing never lands on top of
       them and the arrangement holds at any panel size. */
    /* ⚑ `is-wide` NEEDS REAL WIDTH, NOT JUST A RATIO — 2026-08-24.
       The row layout (flash | words | camera) was chosen on the aspect ratio
       alone, so a 459×91 filler on a 3-up desktop wall scored 5.04 and laid
       three things across a 91px-tall strip: the block rendered 93px tall in a
       91px panel and overflow:hidden sliced it. Both fillers on this wall were
       overflowing — measured on the draft. Same bug, same fix, in
       assets/iq-gallery02.js. */
    function dress(el, h, w) {
      var base;
      if (w / h > 1.45 && w >= 380) {
        el.classList.add('is-wide');
        if (h < 108) { el.classList.add('no-cam'); el.classList.add('no-flash'); return; }
        base = Math.min(h * 0.56, w * 0.20, 300);
      } else {
        var words = h > 520 ? 190 : (h > 300 ? 150 : 112);
        var free = h - 44 - words;
        if (free < 60) { el.classList.add('no-cam'); el.classList.add('no-flash'); return; }
        if (free < 150) { el.classList.add('no-flash'); }
        base = Math.min(w * 0.5, free * 0.66, 360);
        if (base < 38) { el.classList.add('no-cam'); el.classList.add('no-flash'); return; }
      }
      var cam = Math.round(base * CAMK / 100);
      el.style.setProperty('--wim-camw', cam + 'px');
      el.style.setProperty('--wim-flaw', Math.round(base * 0.52 * FLAK / 100) + 'px');
      el.style.setProperty('--wim-camgap', Math.round(Math.max(16, cam * 0.15)) + 'px');
      el.style.setProperty('--wim-flagap', Math.round(Math.max(18, cam * 0.20)) + 'px');
    }

    function layout() {
      if (busy) return;
      busy = true;

      var n = colCount();
      var i;

      if (columns.length !== n) {
        columns.forEach(function (c) { c.remove(); });
        columns = [];
        for (i = 0; i < n; i++) {
          var col = document.createElement('div');
          col.className = 'wim__col';
          grid.appendChild(col);
          columns.push(col);
        }
      } else {
        columns.forEach(function (c) {
          Array.prototype.slice.call(c.querySelectorAll('.wim__filler')).forEach(function (f) { f.remove(); });
        });
      }

      /* Predicted heights are enough to CHOOSE a column — the real measurement
         happens afterwards, so rounding here costs nothing. */
      var gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
      var colw = (grid.getBoundingClientRect().width - (n - 1) * gap) / n;
      var predicted = [];
      for (i = 0; i < n; i++) predicted.push(0);

      cells.forEach(function (cell) {
        var tile = cell.querySelector('.wim__tile');
        var r = parseFloat(tile && tile.getAttribute('data-ar')) || 1;
        var c = 0;
        for (i = 1; i < n; i++) { if (predicted[i] < predicted[c]) c = i; }
        columns[c].appendChild(cell);
        predicted[c] += (colw / r) + gap;
      });

      /* Close each short column so the wall ends level. Under ~90px a filler
         reads as a mistake rather than as breathing room. */
      if (fillerTpl.length) {
        var heights = columns.map(function (c) { return c.getBoundingClientRect().height; });
        var target = Math.max.apply(null, heights);
        var m = 0;
        /* 90px is right for a 3-up desktop column; at 2-up on a phone the
           columns land much closer together and the wall was simply ending
           ragged with no filler at all. */
        var minFill = n <= 2 ? 48 : 90;
        function place(col, h, idx) {
          var src = fillerTpl[idx % fillerTpl.length];
          var el = document.createElement('div');
          el.className = 'wim__filler';
          el.setAttribute('aria-hidden', 'true');
          el.innerHTML = src.innerHTML;
          el.style.height = h + 'px';
          dress(el, h, colw);
          col.appendChild(el);
        }
        for (i = 0; i < n; i++) {
          var deficit = target - heights[i] - gap;
          if (deficit < minFill) continue;
          place(columns[i], deficit, m); m++;
        }
        /* ⚑ A LEVEL WALL STILL WANTS A CLOSING PANEL — 2026-08-24.
           At 2-up on a phone the two columns landed 46px apart, under the 48px
           floor, so NOTHING was placed and the wall simply stopped on a Word —
           which is what "the filler is missing here" looked like. One panel per
           column keeps the bottom flat AND gives the wall a closing line, which
           is what the fillers are for. */
        if (m === 0) {
          var closeH = Math.max(180, Math.round(colw * 0.62));
          for (i = 0; i < n; i++) { place(columns[i], closeH + Math.round(target - heights[i] - gap), i); }
        }
      }

      busy = false;
      startCycle();
      fitFillers();
    }

    /* ⚑ MEASURE, THEN STRIP — 2026-08-24.
       dress() sizes the drawings from arithmetic, but the words are typeset by
       the browser and their real height is only knowable afterwards. A filler is
       a FIXED height (the exact deficit the column has to make up), so anything
       that does not fit is not clipped politely — it is beheaded, and the quote
       starts mid-sentence. Every filler is measured against its own panel and
       stripped back until it fits: lose the flash, then the camera, then a
       smaller face, and finally hide the words rather than show half a line.
       Each step re-measures; nothing here is predicted. */
    function fitFillers() {
      Array.prototype.slice.call(grid.querySelectorAll('.wim__filler')).forEach(function (el) {
        var body = el.querySelector('.wim__filler-body');
        if (!body) return;
        el.classList.remove('is-cramped');
        var room = function () { return el.clientHeight - 16; };
        var over = function () { return body.getBoundingClientRect().height > room(); };
        if (!over()) return;
        el.classList.add('no-flash');
        if (!over()) return;
        el.classList.add('no-cam');
        if (!over()) return;
        el.classList.add('is-cramped');
        if (!over()) return;
        var words = el.querySelector('.wim__filler-words');
        if (words) { words.style.minHeight = '0'; }
        if (!over() || !words) return;
        words.style.display = 'none';
      });
    }

    /* One filler often has to carry several lines. Each panel walks the same
       list from a different starting point, so they never show the same
       sentence at once. */
    function startCycle() {
      if (cycleTimer) { clearInterval(cycleTimer); cycleTimer = null; }
      if (!CYCLE || LINES.length < 2) return;
      var groups = Array.prototype.slice.call(grid.querySelectorAll('.wim__filler-words'))
        .map(function (box) { return { box: box, text: box.querySelector('.wim__filler-text') }; })
        .filter(function (g) { return g.text; });
      if (!groups.length) return;

      /* Pin each block to the tallest line it will ever show, measured in
         place — otherwise a two-line quote following a three-line one
         re-centres the whole stack mid-fade and the drawings shuffle. */
      groups.forEach(function (g) {
        var keep = g.text.textContent, tallest = 0;
        g.box.style.minHeight = '';
        LINES.forEach(function (line) {
          g.text.textContent = line;
          tallest = Math.max(tallest, g.box.getBoundingClientRect().height);
        });
        g.text.textContent = keep;
        g.box.style.minHeight = Math.ceil(tallest) + 'px';
      });

      var step = 0;
      cycleTimer = setInterval(function () {
        step++;
        groups.forEach(function (g, i) {
          g.box.classList.add('is-out');
          setTimeout(function () {
            g.text.textContent = LINES[(step + i) % LINES.length];
            g.box.classList.remove('is-out');
          }, 700);
        });
      }, CYCLE);
    }

    /* --------------------------------------------------- the framed print */

    function mountWall(tile) {
      if (!tile || tile.getAttribute('data-wall-on')) return;
      var src = tile.getAttribute('data-wall');
      if (!src) return;
      tile.setAttribute('data-wall-on', '1');

      var img = document.createElement('img');
      img.className = 'wim__wall';
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      img.decoding = 'async';
      var v = tile.getAttribute('data-wall-srcset'); if (v) img.srcset = v;
      v = tile.getAttribute('data-wall-sizes'); if (v) img.sizes = v;
      img.src = src;
      var flash = tile.querySelector('.wim__flash');
      if (flash) { tile.insertBefore(img, flash); } else { tile.appendChild(img); }
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          var t = e.target;
          idle(function () { mountWall(t); });
        });
      }, { rootMargin: '250px 0px' });
      cells.forEach(function (c) {
        var t = c.querySelector('.wim__tile');
        if (t && t.getAttribute('data-wall')) io.observe(t);
      });
    } else {
      cells.forEach(function (c) { mountWall(c.querySelector('.wim__tile')); });
    }

    /* Touch: a swipe across a tile moves between the Word and the framed
       print, which is what the two dots are announcing. A tap still opens the
       card — and a swipe ends in a click, so that one is swallowed. */
    cells.forEach(function (cell) {
      var tile = cell.querySelector('.wim__tile');
      if (!tile) return;
      var dots = tile.querySelector('.wim__dots');
      var swiped = false;

      ['pointerenter', 'touchstart', 'focus'].forEach(function (ev) {
        tile.addEventListener(ev, function () { mountWall(tile); }, { passive: true });
      });

      tile.addEventListener('click', function () {
        if (swiped) { swiped = false; return; }
        open(cells.indexOf(cell));
      });

      if (!dots) return;
      var x0 = 0, y0 = 0;
      tile.addEventListener('touchstart', function (e) {
        var t = e.changedTouches[0]; x0 = t.clientX; y0 = t.clientY; swiped = false;
      }, { passive: true });
      tile.addEventListener('touchend', function (e) {
        var t = e.changedTouches[0];
        var dx = t.clientX - x0, dy = t.clientY - y0;
        if (Math.abs(dx) < 34 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
        swiped = true;
        tile.classList.toggle('is-wall');
        var on = tile.classList.contains('is-wall') ? 1 : 0;
        Array.prototype.slice.call(dots.children).forEach(function (d, k) {
          if (k === on) { d.classList.add('is-on'); } else { d.classList.remove('is-on'); }
        });
      }, { passive: true });
    });

    /* ---------------------------------------------------------------- find */

    /* Type a Word, land on it. At the current wall size the wall is browsable;
       at several times that it is not, and the masonry means alphabetical order
       is not spatial order — column 1 holds A, D, G… so scanning for "Love" is
       genuinely hard. */
    (function wireFind() {
      var wrap = root.querySelector('.wim__find');
      if (!wrap) return;
      var input = wrap.querySelector('.wim__find-input');
      var list = wrap.querySelector('.wim__find-list');
      var none = wrap.querySelector('.wim__find-none');
      if (!input || !list) return;

      var items = cells.map(function (cell) {
        var w = cell.getAttribute('data-word') || '';
        var store = cell.querySelector('.wim__store');
        var photo = '';
        var em = store && store.querySelector('.wim__cross a');
        if (em) photo = em.textContent.trim();
        return { cell: cell, word: w, key: w.toLowerCase(), photo: photo };
      });

      var shown = [];
      var active = -1;

      function close() {
        list.hidden = true;
        list.innerHTML = '';
        shown = [];
        active = -1;
        input.setAttribute('aria-expanded', 'false');
      }

      function pulse(cell) {
        cell.classList.remove('is-found');
        /* reflow, or re-adding the class in the same frame does nothing */
        void cell.offsetWidth;
        cell.classList.add('is-found');
        var done = function () {
          cell.classList.remove('is-found');
          cell.removeEventListener('animationend', done);
        };
        cell.addEventListener('animationend', done);
        /* belt and braces: animationend never fires under reduced motion */
        setTimeout(done, 6000);
      }

      function go(item) {
        if (!item) return;
        close();
        input.blur();
        try {
          item.cell.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) {
          item.cell.scrollIntoView();
        }
        /* let the scroll settle, or the pulse plays off-screen */
        setTimeout(function () { pulse(item.cell); }, 420);
      }

      function mark(word, q) {
        var i = word.toLowerCase().indexOf(q);
        if (i < 0) return word;
        return word.slice(0, i) + '<b>' + word.slice(i, i + q.length) + '</b>' + word.slice(i + q.length);
      }

      function render() {
        var q = input.value.trim().toLowerCase();
        if (!q) { close(); none.hidden = true; return; }

        /* starts-with first, then anywhere — typing "re" should offer Real
           before Presence */
        var starts = items.filter(function (it) { return it.key.indexOf(q) === 0; });
        var within = items.filter(function (it) { return it.key.indexOf(q) > 0; });
        shown = starts.concat(within).slice(0, 8);

        none.hidden = shown.length > 0;
        if (!shown.length) { list.hidden = true; list.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }

        list.innerHTML = shown.map(function (it, i) {
          return '<li role="option" data-i="' + i + '" aria-selected="false">' +
            '<span>' + mark(it.word, q) + '</span>' +
            (it.photo ? '<em>' + it.photo + '</em>' : '') + '</li>';
        }).join('');
        list.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        active = -1;
      }

      function highlight(n) {
        var lis = list.querySelectorAll('li');
        if (!lis.length) return;
        if (active >= 0 && lis[active]) { lis[active].classList.remove('is-active'); lis[active].setAttribute('aria-selected', 'false'); }
        active = (n + lis.length) % lis.length;
        lis[active].classList.add('is-active');
        lis[active].setAttribute('aria-selected', 'true');
        lis[active].scrollIntoView({ block: 'nearest' });
      }

      input.addEventListener('input', render);
      input.addEventListener('focus', function () { if (input.value.trim()) render(); });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { close(); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); if (list.hidden) render(); highlight(active + 1); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); highlight(active - 1); return; }
        if (e.key === 'Enter') {
          e.preventDefault();
          go(shown[active >= 0 ? active : 0]);
        }
      });

      list.addEventListener('click', function (e) {
        var li = e.target.closest('li[data-i]');
        if (li) go(shown[parseInt(li.getAttribute('data-i'), 10)]);
      });

      document.addEventListener('click', function (e) {
        if (!wrap.contains(e.target)) close();
      });
    })();

    /* ---------------------------------------------------------------- card */

    var lb = null;
    var openIdx = -1;
    var lockY = 0;

    function lockPage() {
    if (document.body.classList.contains('wim-locked')) return;   /* INT 2026-08-30: already pinned, so pageYOffset is 0 and re-reading it would
                                                          destroy the saved position. Next/Prev re-enter open() without closing. */
      lockY = window.pageYOffset || document.documentElement.scrollTop || 0;
      document.body.style.top = (-lockY) + 'px';
      document.body.classList.add('wim-locked');
    }
    /* The theme sets scroll-behavior:smooth, so a plain scrollTo animates the
       page up from 0 and back down again on every close. Pin it to instant for
       the one call, then hand the theme its setting back. */
    function unlockPage() {
      document.body.classList.remove('wim-locked');
      document.body.style.top = '';
      var root = document.documentElement;
      var prev = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, lockY);
      root.style.scrollBehavior = prev;
    }

    function wireCard(panel) {
      var tabs = Array.prototype.slice.call(panel.querySelectorAll('.wim__views button'));
      var views = Array.prototype.slice.call(panel.querySelectorAll('.wim__view'));
      var inks = Array.prototype.slice.call(panel.querySelectorAll('.wim__ink'));
      var inkBar = panel.querySelector('.wim__inks');
      var paper = panel.querySelector('.wim__paper');
      /* Every view image that has a per-paper twin. Was hard-wired to the
         wall view; since 2026-08-21 the Word view is a flat print image with
         the same data-src / data-src-linen pair, so this is now generic.
         An element without data-src-linen (On a Piece) is not in the list and
         is never re-pointed by the paper dots. */
      var paperImgs = Array.prototype.slice.call(
        panel.querySelectorAll('.wim__view img[data-src-linen]')
      );
      var frameBtns = Array.prototype.slice.call(panel.querySelectorAll('.wim__btn[data-carbon]'));
      var stage = panel.querySelector('.wim__art') || panel;
      var ink = 'carbon';
      var pending = 0;

      /* The url the card will actually ask for. warm() and show() MUST agree
         on it or the click pays for a second download. */
      function wantFor(el) {
        var raw = ink === 'linen' && el.getAttribute('data-src-linen')
          ? el.getAttribute('data-src-linen')
          : el.getAttribute('data-src');
        return raw ? sizedSrc(raw, artPx(el)) : '';
      }

      /* Fetch it into the browser cache without touching the DOM. */
      function warm(el) {
        if (!el) return;
        var want = wantFor(el);
        if (!want) return;
        var im = new Image();
        im.decoding = 'async';
        im.src = want;
      }

      function show(el) {
        if (!el) return;
        var want = wantFor(el);
        if (want && el.getAttribute('src') !== want) el.src = want;
      }

      function reveal(want) {
        tabs.forEach(function (t) { t.classList.toggle('is-on', t.getAttribute('data-view') === want); });
        views.forEach(function (v) { v.classList.toggle('is-on', v.getAttribute('data-view') === want); });
        /* The paper switch belongs to the Word and to the framed print; it
           means nothing on a hoodie. Hide it, but KEEP ITS SPACE — display:none
           here shortened the art column by ~41px and the whole card jumped
           every time you touched On a Piece. */
        if (inkBar) inkBar.style.visibility = (want === 'piece') ? 'hidden' : '';
      }

      function setView(want) {
        var view = views.filter(function (v) { return v.getAttribute('data-view') === want; })[0];
        var img = view && view.querySelector('img');

        /* Light the tab immediately — only the picture is allowed to wait. */
        tabs.forEach(function (t) { t.classList.toggle('is-on', t.getAttribute('data-view') === want); });

        if (!img) { reveal(want); return; }
        show(img);
        if (img.complete && img.naturalWidth > 0) { reveal(want); return; }

        /* Nothing on screen yet (first open) — there is no outgoing view to
           hold, so swap now and let it paint when it paints. */
        var current = views.filter(function (v) { return v.classList.contains('is-on'); })[0];
        if (!current) { reveal(want); return; }

        /* Hold the view we are on rather than swapping to an empty panel, and
           dim it so the click still reads as having done something. Inline
           styles only: this section's CSS lives in the Liquid, not here. */
        var token = ++pending;
        stage.style.transition = 'opacity .18s ease';
        stage.style.opacity = '0.5';

        var done = function () {
          if (token !== pending) return;
          stage.style.opacity = '';
          reveal(want);
        };
        /* never hold the card hostage to a slow or broken image */
        var bail = setTimeout(done, 2500);
        var finish = function () { clearTimeout(bail); done(); };

        if (img.decode) {
          img.decode().then(finish, finish);
        } else {
          img.addEventListener('load', finish, { once: true });
          img.addEventListener('error', finish, { once: true });
        }
      }

      function setInk(which) {
        ink = which;
        if (paper) {
          paper.style.setProperty('--wim-ink', which === 'linen' ? LINEN : CARBON);
          paper.style.setProperty('--wim-paper', which === 'linen' ? PAPER_LINEN : PAPER_CARBON);
        }
        inks.forEach(function (d) {
          var on = d.getAttribute('data-ink') === which;
          d.classList.toggle('is-on', on);
          d.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        frameBtns.forEach(function (b) {
          var href = b.getAttribute(which === 'linen' ? 'data-linen' : 'data-carbon');
          if (href) b.setAttribute('href', href);
        });
        /* Carbon and Linen are different PHOTOGRAPHS here, not a recolour —
           true of the flat print and of the room shot alike. */
        paperImgs.forEach(function (im) {
          var v = im.parentNode;
          while (v && v.className.indexOf('wim__view') === -1) v = v.parentNode;
          if (v && v.classList.contains('is-on')) show(im);
          /* This used to removeAttribute('src'), which threw away an image
             already on the wire and made every paper toggle re-download it.
             show() re-points the element by url anyway, so warm the new paper
             instead and leave the old pixels in place until it lands. */
          else warm(im);
        });
      }

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () { setView(tab.getAttribute('data-view')); });
        /* Hover is 200–400 ms of free head start before the click arrives. */
        ['pointerenter', 'focus'].forEach(function (ev) {
          tab.addEventListener(ev, function () {
            var v = views.filter(function (x) { return x.getAttribute('data-view') === tab.getAttribute('data-view'); })[0];
            if (v) warm(v.querySelector('img'));
          }, { passive: true });
        });
      });
      inks.forEach(function (d) {
        d.addEventListener('click', function () { setInk(d.getAttribute('data-ink')); });
      });

      /* INT 2026-08-30, Nils: "The images in the Photo Wall cards can not be
         swiped like in Word wall." A sideways swipe across the picture now steps
         through the same views the buttons offer, on BOTH cards, and it does it
         by firing the button's own click so there is exactly one place that
         changes state. The thresholds are the wall tile's — 34px, and clearly
         more sideways than down — so the gesture feels the same in the card as
         it does on the wall, and a vertical scroll can never flip the view. */
      var _stage = panel.querySelector('.wim__stage');
      if (_stage && tabs.length > 1) {
        var _sx = 0, _sy = 0;
        _stage.addEventListener('touchstart', function (e) {
          var t = e.changedTouches[0]; _sx = t.clientX; _sy = t.clientY;
        }, { passive: true });
        _stage.addEventListener('touchend', function (e) {
          var t = e.changedTouches[0], dx = t.clientX - _sx, dy = t.clientY - _sy;
          if (Math.abs(dx) < 34 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
          var cur = 0;
          tabs.forEach(function (b, k) { if (b.classList.contains('is-on')) cur = k; });
          tabs[(cur + (dx < 0 ? 1 : -1) + tabs.length) % tabs.length].click();
        }, { passive: true });
      }

      setInk(DEFAULT_PAPER);
      setView('word');

      /* Warm whatever the card is not showing, once it has settled. */
      idle(function () {
        views.forEach(function (v) {
          if (v.classList.contains('is-on')) return;
          warm(v.querySelector('img'));
        });
        /* …and the OTHER paper of every per-paper image. warm() only ever
           fetches the paper that is currently selected, so before 2026-08-21
           the first tap on the opposite ink dot always downloaded from cold
           and the frame went white while it landed. Tolerable when that was
           only the wall view; view 1 is a photograph now AND it is the view
           the card opens on, so it is the first thing anyone sees. Two extra
           images per open, both already sized exactly as the stage will ask
           for them, so the swap comes out of cache. */
        paperImgs.forEach(function (im) {
          var other = im.getAttribute(ink === 'linen' ? 'data-src' : 'data-src-linen');
          if (!other) return;
          var pre = new Image();
          pre.decoding = 'async';
          pre.src = sizedSrc(other, artPx(im));
        });
      });
    }

    function open(i) {
      if (i < 0 || i >= cells.length) return;
      if (lb) { lb.remove(); lb = null; }

      var store = cells[i].querySelector('.wim__store');
      if (!store) return;

      lb = document.createElement('div');
      lb.className = 'wim-lb';
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.setAttribute('aria-label', store.getAttribute('data-word') || 'Word of Intention');
      lb.style.setProperty('--wim-lb-accent', ACCENT);

      var panel = document.createElement('div');
      panel.className = 'wim-lb__panel';
      panel.innerHTML = store.innerHTML;

      /* The nav belongs to the READ column, not the panel. On mobile the panel
         is a single column and a panel-anchored nav lands on top of the
         artwork; anchored to the read column it sits beside the kicker, which
         is where Gallery02 puts it. */
      var read = panel.querySelector('.wim__read') || panel;
      var nav = document.createElement('div');
      nav.className = 'wim-lb__nav';
      nav.innerHTML =
        '<button type="button" class="is-prev" aria-label="' + (L.prev || 'Previous') + '">' + ICON_PREV + '</button>' +
        '<span class="wim-lb__count">' + (i + 1) + ' / ' + cells.length + '</span>' +
        '<button type="button" class="is-next" aria-label="' + (L.next || 'Next') + '">' + ICON_NEXT + '</button>' +
        '<button type="button" class="is-close" aria-label="' + (L.close || 'Close') + '">' + ICON_CLOSE + '</button>';
      read.appendChild(nav);

      lb.appendChild(panel);
      document.body.appendChild(lb);
      lockPage();
      openIdx = i;

      wireCard(panel);

      nav.querySelector('.is-prev').addEventListener('click', function () { step(-1); });
      nav.querySelector('.is-next').addEventListener('click', function () { step(1); });
      nav.querySelector('.is-close').addEventListener('click', close);
      /* INT 2026-08-30: same move as gallery02 — the close rides the panel, top
         right over the picture, instead of sitting halfway down the card. */
      var closeBtn = nav.querySelector('.is-close');
      if (closeBtn) { closeBtn.classList.add('wim-lb__x'); panel.appendChild(closeBtn); }
      lb.addEventListener('click', function (e) { if (e.target === lb) close(); });
      if (closeBtn) closeBtn.focus();
    }

    function step(d) {
      var n = (openIdx + d + cells.length) % cells.length;
      openIdx = -1;
      open(n);
    }

    function close() {
      if (lb) { lb.remove(); lb = null; }
      unlockPage();
      if (openIdx >= 0 && cells[openIdx]) {
        var t = cells[openIdx].querySelector('.wim__tile');
        if (t) t.focus({ preventScroll: true });
      }
      openIdx = -1;
    }

    document.addEventListener('keydown', function (e) {
      if (!lb) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    });

    /* ------------------------------------------------- deep link (?word=)
       Nils, 2026-09-01. The Photo Art card links here as
       /pages/words-of-intention?word=fortitude, so an arrival has to land on
       that word rather than at the top of 35 tiles. Scroll, pulse, open the
       card. Handleised compare, because data-word carries the display word
       ("Circle of Life") and the link carries the collection handle.
       The parameter is removed from the address bar afterwards so a reload or
       a shared URL does not re-open the card. */
    (function wireDeepLink() {
      var q = new URLSearchParams(window.location.search).get('word');
      if (!q && window.location.hash.indexOf('#word-') === 0) {
        q = window.location.hash.slice(6);
      }
      if (!q) return;
      var want = String(q).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      if (!want) return;

      var idx = -1;
      cells.forEach(function (cell, i) {
        if (idx > -1) return;
        var w = (cell.getAttribute('data-word') || '')
          .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        if (w === want) idx = i;
      });
      if (idx < 0) return;

      var cell = cells[idx];
      /* Nils, 2026-09-01: land on the word and PULSE it, do not open the card.
         Arriving to a modal you did not ask for is jarring, and it takes the back
         button with it.

         2026-09-01, second pass, Nils: "A little slow". The find box scrolls
         SMOOTHLY because you are on the page watching the wall travel. Arriving
         from another page there is nothing to watch, so this one JUMPS. Two
         animation frames is all the wait needed for layout() to have run, in
         place of the old 260ms guess, and with no scroll animation in flight the
         pulse can start almost at once instead of 420ms later. About 680ms of
         deliberate delay removed.

         The pulse itself is the find box's recipe: drop the class, force a
         reflow so re-adding it actually restarts the animation, add it back,
         clear on animationend with a timeout as belt and braces because
         animationend never fires under reduced motion. */
      var land = function () {
        try {
          cell.scrollIntoView({ behavior: 'auto', block: 'center' });
        } catch (e) {
          cell.scrollIntoView();
        }
        setTimeout(function () {
          cell.classList.remove('is-found');
          void cell.offsetWidth;
          cell.classList.add('is-found');
          var done = function () {
            cell.classList.remove('is-found');
            cell.removeEventListener('animationend', done);
          };
          cell.addEventListener('animationend', done);
          setTimeout(done, 6000);
        }, 80);
      };
      if (window.requestAnimationFrame) {
        requestAnimationFrame(function () { requestAnimationFrame(land); });
      } else {
        setTimeout(land, 60);
      }

      try {
        var u = new URL(window.location.href);
        u.searchParams.delete('word');
        history.replaceState(null, '', u.pathname + (u.search || '') + '');
      } catch (e) { /* older browser, leave the address bar alone */ }
    })();

    /* --------------------------------------------------------------- boot */

    layout();
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { layout(); }).observe(grid);
    }
    window.addEventListener('resize', layout);
    window.addEventListener('load', layout);
  }

  function start() {
    var nodes = document.querySelectorAll('[data-wim-cfg]');
    Array.prototype.forEach.call(nodes, function (root) {
      if (root.getAttribute('data-wim-on')) return;
      root.setAttribute('data-wim-on', '1');
      var el = document.getElementById(root.getAttribute('data-wim-cfg'));
      var cfg = {};
      if (el) { try { cfg = JSON.parse(el.textContent); } catch (e) { cfg = {}; } }
      woiWall(root, cfg);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  /* the theme editor rebuilds a section in place */
  document.addEventListener('shopify:section:load', start);
})();